import { useQueries } from "@tanstack/react-query"
import { Outlet } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Separator } from "design-system/atoms/Separator"
import { cn } from "design-system/utils"
import { formatDate } from "lopermut"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { Pagination } from "modules/shared/components/Pagination"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { conversationTicketsQuery } from "modules/support/lib/conversationTicketsQuery"
import { supportIssuesQuery } from "modules/support/lib/supportIssuesQuery"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/support/list/route"
import { SupportSortFields } from "schema"
import { z } from "zod"
import { ConversationTicketsTable } from "../ConversationTickets/ConversationTicketsTable"
import { IssuesTable } from "./IssuesTable"

const ITEMS_PER_PAGE = 30

type Props = {
  fullPath: string
  search: z.infer<typeof SearchSchema>
}

/**
 * Issues only exist since the new support flow was enabled. To keep older data reachable during
 * the rollout, tickets created before the flag are listed right after the issues, as if both were
 * a single list sharing the same pagination.
 */
export function Issues({ fullPath, search }: Props) {
  const { t } = useTranslation()
  const { enabledAt } = useNewSupportFlow()
  const [issuesCount, setIssuesCount] = useState<number>()
  const [ticketsCount, setTicketsCount] = useState<number>()

  const { page, setPage } = usePagination({ search, fullPath, total: ITEMS_PER_PAGE })

  const { onSort, sortBy, fields } = useSortBy<typeof SupportSortFields>({
    search,
    fullPath,
    fields: ["createdAt"],
  })

  // Legacy tickets do not have Issue urgency or lifecycle status.
  const includeLegacyTickets =
    enabledAt !== null && !search.filterBy?.urgency && !search.filterBy?.issueStatus?.length
  const baseQuery = { sortBy, query: search.query, total: ITEMS_PER_PAGE }
  const pageStart = (page - 1) * ITEMS_PER_PAGE

  // Issues come first, so their window is always aligned with the current page
  const issueWindow = getWindow({ start: pageStart, length: ITEMS_PER_PAGE })
  const issues = useQueries({
    queries: issueWindow.pages.map((issuesPage) =>
      supportIssuesQuery({
        query: {
          ...baseQuery,
          page: issuesPage,
          filterBy: {
            ...search.filterBy,
            createdFrom: enabledAt ?? undefined,
          },
        },
        onSuccess: (data) => setIssuesCount(data.pageInfo.count),
      }),
    ),
    combine: (results) => combineWindow(results, issueWindow),
  })

  // Tickets fill whatever room the issues leave, starting once the issues are exhausted
  const issuesOnPage = Math.max(0, Math.min(ITEMS_PER_PAGE, (issuesCount ?? 0) - pageStart))
  const ticketWindow = getWindow({
    start: Math.max(0, pageStart - (issuesCount ?? 0)),
    length: ITEMS_PER_PAGE - issuesOnPage,
  })
  const tickets = useQueries({
    queries: ticketWindow.pages.map((ticketsPage) => ({
      ...conversationTicketsQuery({
        query: {
          ...baseQuery,
          page: ticketsPage,
          filterBy: { ...search.filterBy, createdBefore: enabledAt ?? undefined },
        },
        onSuccess: (data) => setTicketsCount(data.pageInfo.count),
      }),
      enabled: includeLegacyTickets && issuesCount !== undefined,
    })),
    combine: (results) => combineWindow(results, ticketWindow),
  })

  const count =
    issuesCount === undefined
      ? undefined
      : issuesCount + (includeLegacyTickets ? (ticketsCount ?? 0) : 0)
  const totalPages = count ? Math.ceil(count / ITEMS_PER_PAGE) : undefined
  const showTickets = includeLegacyTickets && (tickets.rows?.length ?? 0) > 0
  const showIssues = !showTickets || issuesOnPage > 0

  return (
    <>
      <div className="flex flex-col gap-4">
        {showIssues && (
          <IssuesTable
            issues={issues.rows}
            isPending={issues.isPending}
            fields={fields}
            onSort={onSort}
            search={search}
          />
        )}
        {showTickets && (
          <div className={cn("flex flex-col gap-3", showIssues && "mt-6")}>
            <div className="relative flex items-center justify-center">
              <Separator className="absolute inset-x-0" />
              <Badge
                variant="outline"
                className="relative rounded-full bg-muted px-3 py-1 font-normal text-muted-foreground"
              >
                {t("support.issues.legacyTicketsNote", {
                  date: enabledAt ? formatDate(enabledAt, "P") : "",
                })}
              </Badge>
            </div>
            <ConversationTicketsTable
              tickets={tickets.rows}
              isPending={tickets.isPending}
              fields={fields}
              onSort={onSort}
              search={search}
            />
          </div>
        )}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          count={count}
        />
      </div>
      <Outlet />
    </>
  )
}

type Window = {
  /** 1-based API pages covering the window (always at least one, so the count is fetched) */
  pages: number[]
  /** Offset of the window inside the first fetched page */
  offset: number
  length: number
}

function getWindow({ start, length }: { start: number; length: number }): Window {
  const firstPage = Math.floor(start / ITEMS_PER_PAGE) + 1
  const offset = start % ITEMS_PER_PAGE
  const needsNextPage = length > 0 && offset + length > ITEMS_PER_PAGE

  return {
    pages: needsNextPage ? [firstPage, firstPage + 1] : [firstPage],
    offset,
    length,
  }
}

function combineWindow<TRow>(results: { data?: TRow[]; isPending: boolean }[], window: Window) {
  if (results.some((result) => result.isPending)) return { rows: undefined, isPending: true }

  const rows = results
    .flatMap((result) => result.data ?? [])
    .slice(window.offset, window.offset + window.length)

  return { rows, isPending: false }
}
